import { useEffect, useState } from 'react'
import { useAuthStore } from '@/store/authStore'
import { getDashboardStats } from '@/api/dashboard'
import { Card, CardContent } from '@/components/ui/card'
import { ActiveCustomersCard } from '@/components/dashboard/ActiveCustomersCard'
import { DailyNewCustomersChart } from '@/components/dashboard/DailyNewCustomersChart'
import { TopSellersCard } from '@/components/dashboard/TopSellersCard'
import { DEFAULT_TOP_SELLERS_PERIOD } from '@/lib/dashboard'
import type { DashboardStats, TopSellersPeriod } from '@/api/types'

export function HomePage(): React.ReactElement {
  const user = useAuthStore((state) => state.user)
  const firstName = user?.display_name?.split(' ')[0] || user?.email?.split('@')[0] || ''

  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [topSellersPeriod, setTopSellersPeriod] = useState<TopSellersPeriod>(DEFAULT_TOP_SELLERS_PERIOD)
  const [isLoading, setIsLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const hasError = errorMessage !== null
  const isInitialLoading = isLoading && stats === null
  const hasStats = !hasError && stats !== null

  useEffect(() => {
    let isCancelled = false

    const loadStats = async (): Promise<void> => {
      setIsLoading(true)
      setErrorMessage(null)

      try {
        const data = await getDashboardStats({ topSellersPeriod })

        if (!isCancelled) {
          setStats(data)
        }
      } catch {
        if (!isCancelled) {
          setErrorMessage('Kunde inte hämta statistiken just nu.')
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    void loadStats()

    return () => {
      isCancelled = true
    }
  }, [topSellersPeriod])

  const handleTopSellersPeriodChange = (period: TopSellersPeriod): void => {
    setTopSellersPeriod(period)
  }

  return (
    <div className="space-y-10 animate-in fade-in duration-500 max-w-6xl mx-auto pb-12">
      <div className="text-center pt-8">
        <h1 className="text-4xl md:text-6xl font-serif text-primary mb-6">
          Välkommen tillbaka <span className="text-accent">{firstName}</span>
        </h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">Överblick över verksamhetens statistik.</p>
      </div>

      {isInitialLoading && <p className="text-center text-sm text-muted-foreground">Laddar statistik...</p>}

      {hasError && (
        <Card className="shadow-none border-destructive/40">
          <CardContent className="pt-6 text-destructive">{errorMessage}</CardContent>
        </Card>
      )}

      {hasStats && (
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-1">
            <ActiveCustomersCard activeCustomers={stats.active_customers} />
            <TopSellersCard
              sellers={stats.top_sellers}
              period={topSellersPeriod}
              isLoading={isLoading}
              onPeriodChange={handleTopSellersPeriodChange}
            />
          </div>
          <div className="lg:col-span-2">
            <DailyNewCustomersChart data={stats.daily_new_customers} />
          </div>
        </div>
      )}
    </div>
  )
}
